"use client"

import { useEffect, type ReactNode } from "react"
import { usePathname, useRouter } from "next/navigation"
import { Loader2 } from "lucide-react"

import { useFetchOnboardingStatus } from "@/hooks/api/onboarding"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useDevAuth } from "@/hooks/use-dev-auth"

const ONBOARDING_ALLOWED_PATHS = [
  "/welcome",
  "/redirect",
  "/impersonation",
  "/onboarding",
  "/logout",
  "/dev-login",
  "/email/unsubscribe",
  "/portal",
  "/mobile/oauth",
]

interface OnboardingGateProps {
  children: ReactNode
}

function isAllowedPath(pathname: string): boolean {
  return ONBOARDING_ALLOWED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

export function OnboardingGate({ children }: OnboardingGateProps) {
  const pathname = usePathname()
  const router = useRouter()
  const { isLoaded, isSignedIn } = useDevAuth()
  const shouldCheckOnboarding = isLoaded && isSignedIn && !isAllowedPath(pathname)

  const { data: user, isLoading: isUserLoading } = useFetchCurrentUser({
    enabled: shouldCheckOnboarding,
    staleTime: 0,
  })
  const { data: onboardingStatus, isLoading: isOnboardingLoading } = useFetchOnboardingStatus({
    enabled: shouldCheckOnboarding,
  })

  const needsOnboarding =
    shouldCheckOnboarding &&
    user?.capabilities.includes("manage_company_settings") === true &&
    (onboardingStatus?.smart_pro === true || onboardingStatus?.solo_trial === true) &&
    !onboardingStatus?.onboarding_finished_at

  useEffect(() => {
    if (!needsOnboarding || isUserLoading || isOnboardingLoading) return

    router.replace("/welcome")
  }, [isOnboardingLoading, isUserLoading, needsOnboarding, router])

  if (!shouldCheckOnboarding) return <>{children}</>

  if (isUserLoading || isOnboardingLoading || needsOnboarding) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    )
  }

  return <>{children}</>
}
